import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSession } from '../../auth/SessionProvider';
import {
  calendarAction,
  useBoard,
  useCalendar,
  useCalendarConnection,
  type CalEvent,
} from '../../lib/data';
import { Icon } from '../../ui/icons';
import { ErrorBox, useDrawer, useToast } from '../../ui/kit';

const HOURS = { from: 7, to: 21 };
const HOUR_PX = 48;

const startOfWeek = (d: Date) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
const hm = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const dayLabel = (d: Date) => d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });

const GOOGLE_RESULT: Record<string, string> = {
  connected: 'Google Calendar connected. Events are syncing.',
  declined: 'Google access was not allowed.',
  expired: 'The connection link expired. Try Connect again.',
  failed: 'Connecting Google Calendar failed. See Integrations.',
  invalid: 'Connecting Google Calendar failed.',
};

function useIsMobile() {
  const [m, setM] = useState(() => window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const q = window.matchMedia('(max-width: 767px)');
    const on = () => setM(q.matches);
    q.addEventListener('change', on);
    return () => q.removeEventListener('change', on);
  }, []);
  return m;
}

export function Calendar() {
  const { can } = useSession();
  const { open } = useDrawer();
  const toast = useToast();
  const mobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const [week, setWeek] = useState(() => startOfWeek(new Date()));
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(week, i)), [week]);
  const from = week.toISOString();
  const to = addDays(week, 7).toISOString();

  const conn = useCalendarConnection();
  const events = useCalendar(from, to, Boolean(conn.data));
  const board = useBoard(can('pipeline'));
  const clinic = (id: string | null) => board.data?.find((l) => l.id === id)?.clinic;

  useEffect(() => {
    const g = params.get('google');
    if (g) {
      toast(GOOGLE_RESULT[g] ?? 'Google Calendar');
      params.delete('google');
      setParams(params, { replace: true });
    }
  }, [params, setParams, toast]);

  const range = `${days[0]!.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} to ${days[6]!.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;

  return (
    <>
      <div className="head">
        <div>
          <h1>
            <span className="grad">Calendar</span>
          </h1>
          <p>
            {conn.data
              ? `Ouradent calendar (${conn.data.account_email}). Changes here go straight to Google and back.`
              : 'The Ouradent Google calendar, inside the desk.'}
          </p>
        </div>
        {conn.data && (
          <button className="btn primary big" onClick={() => open({ type: 'event' })}>
            New event <Icon name="arrow" />
          </button>
        )}
      </div>
      <ErrorBox error={conn.error ?? events.error} />
      {conn.isLoading && <div className="card loading">Loading…</div>}
      {conn.isSuccess && !conn.data && <Connect />}
      {conn.data && !conn.data.gmail && <Connect gmail />}

      {conn.data && (
        <section className="card sec calcard">
          <div className="calbar">
            <div className="seg" role="group" aria-label="Week">
              <button aria-label="Previous week" onClick={() => setWeek(addDays(week, -7))}>
                ‹
              </button>
              <button onClick={() => setWeek(startOfWeek(new Date()))}>Today</button>
              <button aria-label="Next week" onClick={() => setWeek(addDays(week, 7))}>
                ›
              </button>
            </div>
            <b>{range}</b>
            <button
              className="btn sm ghost"
              onClick={() =>
                void calendarAction({ action: 'sync' })
                  .then(() => toast('Calendar is up to date'))
                  .catch((e: Error) => toast(e.message))
              }
            >
              Sync now
            </button>
          </div>
          {events.isLoading ? (
            <div className="loading">Loading…</div>
          ) : mobile ? (
            <Agenda days={days} events={events.data ?? []} clinic={clinic} />
          ) : (
            <Week days={days} events={events.data ?? []} clinic={clinic} />
          )}
        </section>
      )}
    </>
  );
}

function Connect({ gmail = false }: { gmail?: boolean }) {
  const { me } = useSession();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const owner = me?.role === 'owner';
  return (
    <section className="card sec" style={{ padding: 26 }}>
      <b style={{ fontSize: 18 }}>
        {gmail ? 'Allow Gmail (ahoy@ouradent.com)' : 'Connect the Ouradent Google calendar'}
      </b>
      <p className="sub" style={{ padding: 0, margin: '8px 0 16px' }}>
        {gmail
          ? 'Sign in with ahoy@ouradent.com once more and tick the Gmail boxes. After that emails with leads show in their timeline and the call recap can be sent from here.'
          : 'Sign in with ahoy@ouradent.com once. After that every event created in Google shows here within seconds, and everything you add here lands in Google.'}
      </p>
      {owner ? (
        <button
          className="btn primary"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            calendarAction<{ url: string }>({ action: 'connect_url' })
              .then((r) => window.location.assign(r.url))
              .catch((e: Error) => {
                setBusy(false);
                toast(e.message);
              });
          }}
        >
          {busy ? 'Opening Google…' : gmail ? 'Allow Gmail' : 'Connect Google Calendar'}
        </button>
      ) : (
        <p style={{ margin: 0 }}>Ask Viliam or Andrej to {gmail ? 'allow it' : 'connect it'}.</p>
      )}
    </section>
  );
}

type ViewProps = {
  days: Date[];
  events: CalEvent[];
  clinic: (id: string | null) => string | undefined;
};

function EventChip({
  e,
  clinic,
  style,
}: {
  e: CalEvent;
  clinic: ViewProps['clinic'];
  style?: React.CSSProperties;
}) {
  const { open } = useDrawer();
  const lead = clinic(e.lead_id);
  return (
    <button
      className={`calev ${e.lead_id ? 'lead' : ''} ${e.status === 'tentative' ? 'tent' : ''}`}
      style={style}
      onClick={() => open({ type: 'event', id: e.id })}
      aria-label={`${e.summary ?? '(no title)'}, ${e.all_day ? 'all day' : `${hm(e.start_at)} to ${hm(e.end_at)}`}`}
    >
      <b>{e.summary ?? '(no title)'}</b>
      <span>
        {e.all_day ? 'All day' : `${hm(e.start_at)}–${hm(e.end_at)}`}
        {lead ? ` · ${lead}` : ''}
      </span>
    </button>
  );
}

function Week({ days, events, clinic }: ViewProps) {
  const { open } = useDrawer();
  const hours = Array.from({ length: HOURS.to - HOURS.from }, (_, i) => HOURS.from + i);
  const today = new Date();
  return (
    <div className="calweek">
      <div className="calhead">
        <span />
        {days.map((d) => (
          <span key={d.toISOString()} className={sameDay(d, today) ? 'now' : ''}>
            {dayLabel(d)}
          </span>
        ))}
      </div>
      <div className="calallday">
        <span>All day</span>
        {days.map((d) => (
          <div key={d.toISOString()}>
            {events
              .filter((e) => e.all_day && sameDay(new Date(e.start_at), d))
              .map((e) => (
                <EventChip key={e.id} e={e} clinic={clinic} />
              ))}
          </div>
        ))}
      </div>
      <div className="calbody" style={{ height: hours.length * HOUR_PX }}>
        <div className="caltimes">
          {hours.map((h) => (
            <span key={h} style={{ top: (h - HOURS.from) * HOUR_PX }}>
              {String(h).padStart(2, '0')}:00
            </span>
          ))}
        </div>
        {days.map((d) => (
          <div key={d.toISOString()} className="calday">
            {hours.map((h) => (
              <button
                key={h}
                className="calslot"
                style={{ top: (h - HOURS.from) * HOUR_PX, height: HOUR_PX }}
                aria-label={`New event ${dayLabel(d)} ${h}:00`}
                onClick={() => {
                  const s = new Date(d.getFullYear(), d.getMonth(), d.getDate(), h);
                  open({ type: 'event', start: s.toISOString() });
                }}
              />
            ))}
            {events
              .filter((e) => !e.all_day && sameDay(new Date(e.start_at), d))
              .map((e) => {
                const s = new Date(e.start_at);
                const end = new Date(e.end_at);
                const top = Math.max(
                  0,
                  (s.getHours() + s.getMinutes() / 60 - HOURS.from) * HOUR_PX,
                );
                const height = Math.max(
                  38,
                  ((end.getTime() - s.getTime()) / 3_600_000) * HOUR_PX - 2,
                );
                return <EventChip key={e.id} e={e} clinic={clinic} style={{ top, height }} />;
              })}
          </div>
        ))}
      </div>
    </div>
  );
}

function Agenda({ days, events, clinic }: ViewProps) {
  const today = new Date();
  return (
    <div className="calagenda">
      {days.map((d) => {
        const list = events.filter((e) => sameDay(new Date(e.start_at), d));
        return (
          <div key={d.toISOString()} className="calagday">
            <h3 className={sameDay(d, today) ? 'now' : ''}>
              {d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}
            </h3>
            {list.length ? (
              list.map((e) => <EventChip key={e.id} e={e} clinic={clinic} />)
            ) : (
              <p className="calempty">Nothing planned</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
